/**
 * Singleton Shopify Buy Button SDK client + UI. Every cart-touching component
 * (track BUY buttons, the navbar cart toggle, the album page Buy Buttons) must
 * call `getShopifyUI()` from THIS module so they all share one in-memory cart.
 *
 * Without this, the SDK creates one cart per `buildClient()` call, which means
 * clicking BUY on a track adds to one cart, and the navbar icon opens a
 * different (empty) cart. Fixed by routing everything through this singleton.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const SDK_URL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';

let _sdkPromise = null;
const loadSDK = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('window unavailable'));
  if (window.ShopifyBuy && window.ShopifyBuy.UI) return Promise.resolve();
  if (_sdkPromise) return _sdkPromise;
  _sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.async = true;
    s.src = SDK_URL;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Shopify Buy Button SDK'));
    document.head.appendChild(s);
  });
  return _sdkPromise;
};

let _settings = null;
let _settingsPromise = null;
const fetchSettings = () => {
  if (_settings) return Promise.resolve(_settings);
  if (_settingsPromise) return _settingsPromise;
  _settingsPromise = axios
    .get(`${API}/api/homepage`)
    .then((res) => {
      _settings = {
        domain: (res.data && res.data.shopify_shop_domain) || '',
        token: (res.data && res.data.shopify_storefront_token) || '',
      };
      return _settings;
    })
    .catch(() => {
      _settings = { domain: '', token: '' };
      return _settings;
    });
  return _settingsPromise;
};

let _uiPromise = null;

/**
 * Resolves to the singleton ShopifyBuy.UI instance shared across the entire app.
 * Throws if Shopify credentials aren't configured.
 */
export const getShopifyUI = () => {
  if (_uiPromise) return _uiPromise;
  _uiPromise = (async () => {
    const settings = await fetchSettings();
    if (!settings.domain || !settings.token) {
      throw new Error('Shopify credentials not configured');
    }
    await loadSDK();
    const client = window.ShopifyBuy.buildClient({
      domain: settings.domain,
      storefrontAccessToken: settings.token,
    });
    return await window.ShopifyBuy.UI.onReady(client);
  })();
  return _uiPromise;
};

export const getShopifySettings = () => fetchSettings();
